/**
 * OMF Geolocation & Delivery Calculator
 * Handles postcode lookup, nearest store finding, and shipping tier calculation
 *
 * Dependencies:
 * - window.omfPostcodeUrl — URL to postcode CDN JSON (set in theme.liquid)
 * - window.omfStorefrontToken — Shopify Storefront API token (set in theme.liquid)
 */

// ================================================
// CONSTANTS
// ================================================

const POSTCODE_CACHE_KEY = 'omfPostcodeData';
const STORE_LOCATIONS_CACHE_KEY = 'omfStoreLocations';
const STORE_LOCATIONS_CACHE_DATE_KEY = 'omfStoreLocationsCacheDate';
const NEAREST_STORE_KEY = 'omfNearestStore';
const USER_LOCATION_KEY = 'omfUserLocation';

const SHIPPING_TIERS = [
  { max: 15,       rate: 99,  label: 'Delivery to Room' },
  { max: 30,       rate: 120, label: 'Standard Shipping' },
  { max: 50,       rate: 140, label: 'Standard Shipping' },
  { max: 80,       rate: 180, label: 'Standard Shipping' },
  { max: 150,      rate: 250, label: 'Standard Shipping' },
  { max: 300,      rate: 370, label: 'Standard Shipping' },
  { max: Infinity, rate: 370, label: 'Please call your nearest store to confirm' },
];

// ================================================
// POSTCODE LOOKUP
// ================================================

async function getPostcodeData() {
  const cached = sessionStorage.getItem(POSTCODE_CACHE_KEY);
  if (cached) {
    try {
      return JSON.parse(cached);
    } catch (e) {
      sessionStorage.removeItem(POSTCODE_CACHE_KEY);
    }
  }

  const url = window.omfPostcodeUrl;
  if (!url) {
    console.error('OMF: window.omfPostcodeUrl is not defined');
    return [];
  }

  try {
    const response = await fetch(url);
    const data = await response.json();
    sessionStorage.setItem(POSTCODE_CACHE_KEY, JSON.stringify(data));
    return data;
  } catch (e) {
    console.error('OMF: Failed to fetch postcode data', e);
    return [];
  }
}

async function getPostcodeMatches(postcode) {
  const data = await getPostcodeData();
  if (!data.length) return [];
  return data.filter(entry => entry.Postocode === postcode.toString());
}

async function isValidPostcode(postcode) {
  const matches = await getPostcodeMatches(postcode);
  return matches.length > 0;
}

async function getPostcodeLatLng(postcode) {
  const matches = await getPostcodeMatches(postcode);
  if (!matches.length) return null;

  // Skip entries with invalid longitude (must be positive for Australia)
  // PO Box postcodes often have bad coordinate data
  const valid = matches.find(m => parseFloat(m.Longitude) > 0);
  if (!valid) return null;

  return {
    lat: parseFloat(valid.Latitude),
    lng: parseFloat(valid.Longitude),
  };
}

// ================================================
// STORE LOCATIONS — Storefront API
// ================================================

/**
 * Fetch all store locations from Shopify Storefront API
 * Cached in sessionStorage daily so it only fetches once per day
 * @returns {Promise<Array>} Array of parsed store objects
 */
async function fetchStoreLocations() {
  // Check cache — only re-fetch once per day
  const cachedData = sessionStorage.getItem(STORE_LOCATIONS_CACHE_KEY);
  const cacheDate = sessionStorage.getItem(STORE_LOCATIONS_CACHE_DATE_KEY);
  const today = new Date().toDateString();

  if (cachedData && cacheDate === today) {
    try {
      return JSON.parse(cachedData);
    } catch (e) {
      sessionStorage.removeItem(STORE_LOCATIONS_CACHE_KEY);
      sessionStorage.removeItem(STORE_LOCATIONS_CACHE_DATE_KEY);
    }
  }

  const token = window.omfStorefrontToken;
  if (!token) {
    console.error('OMF: window.omfStorefrontToken is not defined');
    return [];
  }

  const query = `
    {
      metaobjects(type: "locations", first: 250) {
        edges {
          node {
            handle
            store_name: field(key: "store_name") { value }
            store_location: field(key: "store_location") { value }
            store_address: field(key: "store_address") { value }
            store_city: field(key: "store_city") { value }
            store_postcode: field(key: "store_postcode") { value }
            store_tel: field(key: "store_tel") { value }
            enable_store: field(key: "enable_store") { value }
            warehouse: field(key: "warehouse") { value }
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(
      `https://${window.Shopify.shop}/api/2024-01/graphql.json`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query }),
      }
    );

    if (!response.ok) {
      throw new Error(`Storefront API error: ${response.status}`);
    }

    const json = await response.json();
    const edges = json.data?.metaobjects?.edges ?? [];

    const stores = edges
      .map(({ node }) => ({
        handle: node.handle,
        name: node.store_name?.value ?? '',
        location: node.store_location?.value ?? '',
        address: node.store_address?.value ?? '',
        city: node.store_city?.value ?? '',
        postcode: node.store_postcode?.value ?? '',
        phone: node.store_tel?.value ?? '',
        enabled: node.enable_store?.value === 'true',
        warehouse: node.warehouse?.value === 'true',
      }))
      .filter(store => store.name && store.location);

    sessionStorage.setItem(STORE_LOCATIONS_CACHE_KEY, JSON.stringify(stores));
    sessionStorage.setItem(STORE_LOCATIONS_CACHE_DATE_KEY, today);

    return stores;
  } catch (e) {
    console.error('OMF: Failed to fetch store locations', e);
    return [];
  }
}

// ================================================
// STORE FILTERING & PARSING
// ================================================

/**
 * Parse the store location field — auto-detects lat/lng order
 * Australian latitude is always negative (-10 to -45)
 * Australian longitude is always positive (113 to 154)
 */
function parseStoreLocation(locationString) {
  if (!locationString) return null;
  const parts = locationString.split(',').map(s => parseFloat(s.trim()));
  if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;

  let lat, lng;
  if (parts[0] < 0 && parts[1] > 0) {
    lat = parts[0];
    lng = parts[1];
  } else {
    lng = parts[0];
    lat = parts[1];
  }

  return { lat, lng };
}

/**
 * Get all active, non-warehouse stores with parsed coordinates
 * @returns {Promise<Array>}
 */
async function getFulfillableStores() {
  const stores = await fetchStoreLocations();

  return stores
    .filter(store => store.enabled === true && store.warehouse !== true)
    .map(store => {
      const coords = parseStoreLocation(store.location);
      return {
        ...store,
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
      };
    })
    .filter(store => store.lat !== null && store.lng !== null);
}

// ================================================
// DISTANCE CALCULATION
// ================================================

function calculateDistance(lat1, lng1, lat2, lng2) {
  const toRad = deg => (deg * Math.PI) / 180;
  const R = 6371;

  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// ================================================
// NEAREST STORE
// ================================================

/**
 * Find the nearest store to a given lat/lng
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<{store: Object, distance: number}|null>}
 */
async function getNearestStore(lat, lng) {
  const stores = await getFulfillableStores();
  if (!stores.length) return null;

  let nearest = null;
  let minDistance = Infinity;

  for (const store of stores) {
    const distance = calculateDistance(lat, lng, store.lat, store.lng);
    if (distance < minDistance) {
      minDistance = distance;
      nearest = store;
    }
  }

  if (!nearest) return null;

  return {
    store: nearest,
    distance: parseFloat(minDistance.toFixed(2)),
  };
}

// ================================================
// SHIPPING TIER
// ================================================

function getShippingTier(distanceKm) {
  for (const tier of SHIPPING_TIERS) {
    if (distanceKm <= tier.max) {
      return { rate: tier.rate, label: tier.label };
    }
  }
  return { rate: 370, label: 'Please call your nearest store to confirm' };
}

// ================================================
// MAIN — CHECK AVAILABILITY
// ================================================

async function checkAvailability(postcode) {
  const postcodeStr = postcode.toString().trim();
  if (!/^\d{4}$/.test(postcodeStr)) {
    return { valid: false, error: 'Please enter a valid 4-digit postcode' };
  }

  const valid = await isValidPostcode(postcodeStr);
  if (!valid) {
    return { valid: false, error: 'Postcode not found. Please check and try again.' };
  }

  const coords = await getPostcodeLatLng(postcodeStr);
  if (!coords) {
    return { valid: false, error: 'Could not locate postcode. Please try again.' };
  }

  const result = await getNearestStore(coords.lat, coords.lng);
  if (!result) {
    return { valid: false, error: 'No stores found. Please contact us for assistance.' };
  }

  const shipping = getShippingTier(result.distance);

  sessionStorage.setItem(NEAREST_STORE_KEY, JSON.stringify(result.store));
  sessionStorage.setItem(USER_LOCATION_KEY, JSON.stringify({ postcode: postcodeStr, ...coords }));

  return {
    valid: true,
    nearestStore: result.store,
    distance: result.distance,
    shipping,
  };
}

// ================================================
// EXPORTS
// ================================================
window.omfGeo = {
  checkAvailability,
  getPostcodeMatches,
  getNearestStore,
  getShippingTier,
  calculateDistance,
  getFulfillableStores,
};